import { z } from 'zod';

import {
  AgentActionToolSchema,
  AgentCodeToolSchema,
  AgentHookpipeToolSchema,
  AgentRunFlowToolSchema,
  HOOKPIPE_ACTIONS,
  type TAgentToolKind,
} from './agent-config.schema';
import { HOOKPIPE_ACTION_PARAMS, hookpipeChangesData } from './agent-tools';

export interface IAgentToolFieldLabel {
  path: string;
  label: string;
  help?: string;
  kinds?: readonly TAgentToolKind[];
}

// The tool panel's words (Д30), so a finding says “Name the model sees”, not “Tools”.
export const AGENT_TOOL_FIELDS: readonly IAgentToolFieldLabel[] = [
  {
    path: 'name',
    label: 'Name the model sees',
    help: 'Lowercase letters, digits and _, up to 64; unique in the step.',
  },
  {
    path: 'description',
    label: 'Description for the model',
    help: 'What it does and when to use it.',
  },
  { path: 'fields', label: 'Fields' },
  { path: 'fields.*.path', label: 'Field name' },
  { path: 'fields.*.fill', label: 'Who fills it' },
  { path: 'fields.*.type', label: 'Type' },
  { path: 'fields.*.description', label: 'What the agent should put there' },
  { path: 'fields.*.value', label: 'Fixed value' },
  { path: 'changesData', label: 'It changes data' },
  { path: 'mode', label: 'When the agent calls it' },
  { path: 'config.credentialId', label: 'Account', kinds: ['action'] },
  { path: 'config.input', label: 'Input', kinds: ['action'] },
  { path: 'config.itemId', label: 'Code from the Library', kinds: ['code'] },
  { path: 'config.versionId', label: 'Code from the Library', kinds: ['code'] },
  { path: 'config.version', label: 'Code from the Library', kinds: ['code'] },
  { path: 'config.flowId', label: 'Flow', kinds: ['run_flow'] },
  {
    path: 'config.waitSec',
    label: 'Wait for the result up to',
    help: 'Past it the agent hears “still running”; the flow finishes on its own.',
    kinds: ['run_flow'],
  },
  { path: 'config.action', label: 'Action', kinds: ['hookpipe'] },
  {
    path: 'config.flowIds',
    label: 'Flows it may touch',
    help: 'For the agent no other flow exists.',
    kinds: ['hookpipe'],
  },
  { path: 'config.maxCopies', label: 'At most', kinds: ['hookpipe'] },
  { path: 'config.waitSec', label: 'Wait for the job up to', kinds: ['hookpipe'] },
];

export interface IAgentToolKindEntry {
  kind: TAgentToolKind;
  label: string;
  description: string;
  /** `null`: decided by the tool's own settings (an HTTP method, a Hookpipe action). */
  changesData: boolean | null;
  askable: boolean;
  configJsonSchema: unknown;
  fields: { path: string; label: string; help?: string }[];
  actions?: {
    key: string;
    changesData: boolean;
    fields: { path: string; type: string; required: boolean; description: string }[];
  }[];
}

const schema = (value: z.ZodType) => z.toJSONSchema(value, { io: 'input', unrepresentable: 'any' });

const fieldsOf = (kind: TAgentToolKind): IAgentToolKindEntry['fields'] =>
  AGENT_TOOL_FIELDS.filter((each) => !each.kinds || each.kinds.includes(kind)).map(
    ({ path, label, help }) => ({ path, label, ...(help ? { help } : {}) }),
  );

/** What «Add a tool» offers (agents §7, Д30): the panel's forms come from here, not by hand. */
export function agentToolKindCatalog(): IAgentToolKindEntry[] {
  return [
    {
      kind: 'action',
      label: 'App action',
      description: 'HTTP, Slack, Telegram or Email: the action a step would run.',
      changesData: null,
      askable: true,
      configJsonSchema: schema(AgentActionToolSchema),
      fields: fieldsOf('action'),
    },
    {
      kind: 'code',
      label: 'Code',
      description: 'A pinned Code version from the Library, run in the sandbox.',
      changesData: false,
      askable: false,
      configJsonSchema: schema(AgentCodeToolSchema),
      fields: fieldsOf('code'),
    },
    {
      kind: 'run_flow',
      label: 'Run a flow',
      description: 'Starts a manual flow and hands back what it made.',
      changesData: true,
      askable: true,
      configJsonSchema: schema(AgentRunFlowToolSchema),
      fields: fieldsOf('run_flow'),
    },
    {
      kind: 'hookpipe',
      label: 'Hookpipe action',
      description: 'Reads issues and runs of chosen flows, mutes an issue, redelivers failures.',
      changesData: null,
      askable: true,
      configJsonSchema: schema(AgentHookpipeToolSchema),
      fields: fieldsOf('hookpipe'),
      actions: HOOKPIPE_ACTIONS.map((key) => ({
        key,
        changesData: hookpipeChangesData(key),
        fields: HOOKPIPE_ACTION_PARAMS[key].map((param) => ({
          path: param.path,
          type: param.type,
          required: param.required,
          description: param.description,
        })),
      })),
    },
  ];
}
